import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Input } from "../components/ui/input";
import { Checkbox } from "../components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { toast } from "sonner";
import {
  Mail,
  Users,
  CheckCircle,
  Clock,
  LogOut,
  User,
  Eye,
  Check,
  X,
  TrendingUp,
  Award,
  Search,
  BarChart3,
  Activity,
  Download,
  ChevronRight,
  UserPlus,
  FileCheck,
  XCircle,
  Calendar,
  ArrowUpRight,
  ArrowDownRight,
  Scale,
  HelpCircle
} from "lucide-react";
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  Legend
} from "recharts";

export default function TeamManagerDashboard() {
  const { user, logout, token } = useAuth();
  const navigate = useNavigate();
  const [workers, setWorkers] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [teamStats, setTeamStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submissionFilter, setSubmissionFilter] = useState("all");
  const [workerSearch, setWorkerSearch] = useState("");
  
  // New state for enhancements
  const [activityFeed, setActivityFeed] = useState([]);
  const [performanceTrends, setPerformanceTrends] = useState({ daily: [], weekly: [] });
  const [selectedWorker, setSelectedWorker] = useState(null);
  const [workerDetail, setWorkerDetail] = useState(null);
  const [showWorkerModal, setShowWorkerModal] = useState(false);
  const [selectedSubmissions, setSelectedSubmissions] = useState([]);
  const [compareWorkers, setCompareWorkers] = useState([]);
  const [comparisonData, setComparisonData] = useState(null);
  const [showCompareModal, setShowCompareModal] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [workersRes, submissionsRes, statsRes, activityRes, trendsRes] = await Promise.all([
        axios.get(`${API}/v2/team/workers`, { headers }),
        axios.get(`${API}/v2/submissions`, { headers }),
        axios.get(`${API}/v2/team/stats`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API}/v2/team/activity-feed?limit=20`, { headers }).catch(() => ({ data: { items: [] } })),
        axios.get(`${API}/v2/team/performance-trends?days=30`, { headers }).catch(() => ({ data: { daily: [], weekly: [] } }))
      ]);
      
      setWorkers(workersRes.data.items || workersRes.data || []);
      setSubmissions(submissionsRes.data.items || submissionsRes.data || []);
      setTeamStats(statsRes.data);
      setActivityFeed(activityRes.data.items || []);
      setPerformanceTrends(trendsRes.data || { daily: [], weekly: [] });
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleReviewSubmission = async (submissionId, status) => {
    try {
      await axios.patch(`${API}/v2/submissions/${submissionId}/review`, { status }, { headers });
      toast.success(`Submission ${status}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to review submission");
    }
  };

  const handleBulkReview = async (status) => {
    if (selectedSubmissions.length === 0) {
      toast.error("No submissions selected");
      return;
    }
    try {
      const response = await axios.post(`${API}/v2/team/bulk-review`, {
        submission_ids: selectedSubmissions,
        status
      }, { headers });
      toast.success(response.data.message);
      setSelectedSubmissions([]);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to bulk review");
    }
  };

  const handleExportTeam = async () => {
    try {
      const response = await axios.get(`${API}/v2/team/export`, {
        headers,
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `team_export_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Team data exported");
    } catch (error) {
      toast.error("Failed to export data");
    }
  };

  const handleViewWorkerDetail = async (workerId) => {
    try {
      const response = await axios.get(`${API}/v2/team/worker/${workerId}`, { headers });
      setWorkerDetail(response.data);
      setShowWorkerModal(true);
    } catch (error) {
      toast.error("Failed to load worker details");
    }
  };

  const handleCompareWorkers = async () => {
    if (compareWorkers.length < 2) {
      toast.error("Select at least 2 workers to compare");
      return;
    }
    try {
      const response = await axios.get(`${API}/v2/team/compare?worker_ids=${compareWorkers.join(',')}`, { headers });
      setComparisonData(response.data.workers);
      setShowCompareModal(true);
    } catch (error) {
      toast.error("Failed to compare workers");
    }
  };

  const toggleCompareWorker = (workerId) => {
    setCompareWorkers(prev => 
      prev.includes(workerId) 
        ? prev.filter(id => id !== workerId)
        : prev.length < 5 ? [...prev, workerId] : prev
    );
  };

  const toggleSubmissionSelection = (submissionId) => {
    setSelectedSubmissions(prev =>
      prev.includes(submissionId)
        ? prev.filter(id => id !== submissionId)
        : [...prev, submissionId]
    );
  };

  const selectAllPendingSubmissions = () => {
    const pendingIds = submissions.filter(s => s.status === "pending").map(s => s.submission_id);
    setSelectedSubmissions(prev => 
      prev.length === pendingIds.length ? [] : pendingIds
    );
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const pendingSubmissions = submissions.filter(s => s.status === "pending");
  const approvedSubmissions = submissions.filter(s => s.status === "approved");
  const rejectedSubmissions = submissions.filter(s => s.status === "rejected");

  const filteredSubmissions = submissions.filter(s => {
    if (submissionFilter === "all") return true;
    return s.status === submissionFilter;
  });

  const filteredWorkers = workers.filter(w => {
    if (!workerSearch) return true;
    const search = workerSearch.toLowerCase();
    return w.name?.toLowerCase().includes(search) || w.email?.toLowerCase().includes(search);
  });

  const totalPoints = workers.reduce((sum, w) => sum + (w.points_balance || 0), 0);
  const activeWorkers = workers.filter(w => w.status === "active").length;

  // Format chart data
  const chartData = performanceTrends.daily.slice(-14).map(d => ({
    date: d.date.slice(5),
    submissions: d.submissions,
    approved: d.approved,
    points: d.points_earned
  }));

  const getActivityIcon = (type) => {
    switch (type) {
      case "submission_approved": return <CheckCircle className="w-4 h-4 text-green-500" />;
      case "submission_rejected": return <XCircle className="w-4 h-4 text-red-500" />;
      case "submission_pending": return <Clock className="w-4 h-4 text-amber-500" />;
      case "member_joined": return <UserPlus className="w-4 h-4 text-blue-500" />;
      default: return <Activity className="w-4 h-4 text-slate-400" />;
    }
  };

  const getActivityText = (activity) => {
    switch (activity.type) {
      case "submission_approved":
        return <><span className="font-medium">{activity.worker_name}</span> completed <span className="font-medium">{activity.task_title}</span> (+{activity.points} pts)</>;
      case "submission_rejected":
        return <><span className="font-medium">{activity.worker_name}</span>'s submission for <span className="font-medium">{activity.task_title}</span> was rejected</>;
      case "submission_pending":
        return <><span className="font-medium">{activity.worker_name}</span> submitted <span className="font-medium">{activity.task_title}</span></>;
      case "member_joined":
        return <><span className="font-medium">{activity.worker_name}</span> joined the team</>;
      default:
        return "Activity";
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="team-manager-dashboard">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img 
                src="/images/gigseasy-logo.svg" 
                alt="GigsEasy Logo" 
                className="h-10"
              />
              <div>
                <p className="text-sm text-slate-500">Team Manager Dashboard</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right mr-4">
                <p className="font-medium text-slate-900">{user?.name}</p>
                <p className="text-xs text-slate-500">Team Manager</p>
              </div>
              <Link to="/support">
                <Button variant="ghost" size="icon" data-testid="support-btn" title="Support">
                  <HelpCircle className="w-5 h-5" />
                </Button>
              </Link>
              <Link to="/profile">
                <Button variant="ghost" size="icon" data-testid="profile-btn">
                  <User className="w-5 h-5" />
                </Button>
              </Link>
              <Button variant="ghost" size="icon" onClick={handleLogout} data-testid="logout-btn">
                <LogOut className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
          <Card className="border-slate-200">
            <CardContent className="p-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Users className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{workers.length}</p>
                  <p className="text-xs text-slate-500">Team Members</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200">
            <CardContent className="p-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{activeWorkers}</p>
                  <p className="text-xs text-slate-500">Active Gigsters</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200">
            <CardContent className="p-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                  <Clock className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{pendingSubmissions.length}</p>
                  <p className="text-xs text-slate-500">Pending Review</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200">
            <CardContent className="p-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{approvedSubmissions.length}</p>
                  <p className="text-xs text-slate-500">Completed Tasks</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200">
            <CardContent className="p-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                  <Award className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{totalPoints.toLocaleString()}</p>
                  <p className="text-xs text-slate-500">Total Team Points</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <Tabs defaultValue="submissions" className="space-y-6">
          <TabsList className="bg-white border border-slate-200">
            <TabsTrigger value="submissions" data-testid="submissions-tab" className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              Submissions
              {pendingSubmissions.length > 0 && (
                <Badge className="bg-amber-500 text-white ml-1">{pendingSubmissions.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="team" data-testid="team-tab" className="flex items-center gap-2">
              <Users className="w-4 h-4" />
              My Team
            </TabsTrigger>
            <TabsTrigger value="activity" data-testid="activity-tab" className="flex items-center gap-2">
              <Activity className="w-4 h-4" />
              Activity
            </TabsTrigger>
            <TabsTrigger value="performance" data-testid="performance-tab" className="flex items-center gap-2">
              <BarChart3 className="w-4 h-4" />
              Performance
            </TabsTrigger>
          </TabsList>

          {/* Submissions Tab */}
          <TabsContent value="submissions">
            <Card className="border-slate-200">
              <CardHeader className="border-b border-slate-100">
                <div className="flex items-center justify-between">
                  <CardTitle className="font-heading text-xl">Task Submissions</CardTitle>
                  <div className="flex items-center gap-3">
                    {/* Bulk Actions */}
                    {selectedSubmissions.length > 0 && (
                      <div className="flex items-center gap-2 mr-4">
                        <span className="text-sm text-slate-500">{selectedSubmissions.length} selected</span>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-green-600 border-green-300 hover:bg-green-50"
                          onClick={() => handleBulkReview("approved")}
                          data-testid="bulk-approve-btn"
                        >
                          <Check className="w-4 h-4 mr-1" /> Approve All
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-red-600 border-red-300 hover:bg-red-50"
                          onClick={() => handleBulkReview("rejected")}
                          data-testid="bulk-reject-btn"
                        >
                          <X className="w-4 h-4 mr-1" /> Reject All
                        </Button>
                      </div>
                    )}
                    <Select value={submissionFilter} onValueChange={setSubmissionFilter}>
                      <SelectTrigger className="w-36">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Status</SelectItem>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="approved">Approved</SelectItem>
                        <SelectItem value="rejected">Rejected</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {filteredSubmissions.length === 0 ? (
                  <div className="p-8 text-center text-slate-500">
                    <CheckCircle className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                    <p>No submissions {submissionFilter !== "all" ? `with status "${submissionFilter}"` : "from your team yet"}</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-10">
                          {pendingSubmissions.length > 0 && (
                            <Checkbox
                              checked={selectedSubmissions.length === pendingSubmissions.length && pendingSubmissions.length > 0}
                              onCheckedChange={selectAllPendingSubmissions}
                            />
                          )}
                        </TableHead>
                        <TableHead>Gigster</TableHead>
                        <TableHead>Task</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Points</TableHead>
                        <TableHead>Submitted</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredSubmissions.map((submission) => (
                        <TableRow key={submission.submission_id}>
                          <TableCell>
                            {submission.status === "pending" && (
                              <Checkbox
                                checked={selectedSubmissions.includes(submission.submission_id)}
                                onCheckedChange={() => toggleSubmissionSelection(submission.submission_id)}
                              />
                            )}
                          </TableCell>
                          <TableCell>
                            <div>
                              <p className="font-medium">{submission.worker?.name || "Unknown"}</p>
                              <p className="text-sm text-slate-500">{submission.worker?.email}</p>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div>
                              <p className="font-medium">{submission.task?.title || "Unknown Task"}</p>
                              <p className="text-sm text-slate-500 capitalize">{submission.task?.task_type?.replace("_", " ")}</p>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge
                              className={
                                submission.status === "approved"
                                  ? "bg-green-100 text-green-700"
                                  : submission.status === "rejected"
                                  ? "bg-red-100 text-red-700"
                                  : "bg-amber-100 text-amber-700"
                              }
                            >
                              {submission.status}
                            </Badge>
                          </TableCell>
                          <TableCell className="font-medium text-amber-600">
                            {submission.task?.points_reward || 0} pts
                          </TableCell>
                          <TableCell className="text-slate-500">
                            {submission.submitted_at ? new Date(submission.submitted_at).toLocaleDateString() : "-"}
                          </TableCell>
                          <TableCell>
                            {submission.status === "pending" ? (
                              <div className="flex items-center gap-2">
                                {submission.screenshot_data && (
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    title="View Screenshot"
                                    onClick={() => window.open(submission.screenshot_data, "_blank")}
                                    data-testid={`view-screenshot-${submission.submission_id}`}
                                  >
                                    <Eye className="w-4 h-4" />
                                  </Button>
                                )}
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="text-green-600 hover:text-green-700 hover:bg-green-50"
                                  onClick={() => handleReviewSubmission(submission.submission_id, "approved")}
                                  data-testid={`approve-${submission.submission_id}`}
                                >
                                  <Check className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                  onClick={() => handleReviewSubmission(submission.submission_id, "rejected")}
                                  data-testid={`reject-${submission.submission_id}`}
                                >
                                  <X className="w-4 h-4" />
                                </Button>
                              </div>
                            ) : (
                              <span className="text-sm text-slate-400">Reviewed</span>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Team Tab */}
          <TabsContent value="team">
            <Card className="border-slate-200">
              <CardHeader className="border-b border-slate-100">
                <div className="flex items-center justify-between">
                  <CardTitle className="font-heading text-xl">My Team ({workers.length} Gigsters)</CardTitle>
                  <div className="flex items-center gap-3">
                    {compareWorkers.length >= 2 && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleCompareWorkers}
                        className="mr-2"
                        data-testid="compare-workers-btn"
                      >
                        <Scale className="w-4 h-4 mr-2" />
                        Compare ({compareWorkers.length})
                      </Button>
                    )}
                    <Button variant="outline" size="sm" onClick={handleExportTeam} data-testid="export-team-btn">
                      <Download className="w-4 h-4 mr-2" />
                      Export CSV
                    </Button>
                    <div className="relative w-64">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        placeholder="Search team members..."
                        value={workerSearch}
                        onChange={(e) => setWorkerSearch(e.target.value)}
                        className="pl-10"
                      />
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {filteredWorkers.length === 0 ? (
                  <div className="p-8 text-center text-slate-500">
                    <Users className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                    <p>{workerSearch ? "No team members match your search" : "No gigsters assigned to your team yet"}</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-10">
                          <span className="text-xs text-slate-400">Compare</span>
                        </TableHead>
                        <TableHead>Name</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Tasks Completed</TableHead>
                        <TableHead>Points Balance</TableHead>
                        <TableHead>Joined</TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredWorkers.map((worker) => {
                        const workerSubmissions = submissions.filter(s => s.user_id === worker.user_id);
                        const completedTasks = workerSubmissions.filter(s => s.status === "approved").length;
                        
                        return (
                          <TableRow key={worker.user_id} className="hover:bg-slate-50">
                            <TableCell>
                              <Checkbox
                                checked={compareWorkers.includes(worker.user_id)}
                                onCheckedChange={() => toggleCompareWorker(worker.user_id)}
                                disabled={!compareWorkers.includes(worker.user_id) && compareWorkers.length >= 5}
                              />
                            </TableCell>
                            <TableCell className="font-medium">{worker.name || "Unknown"}</TableCell>
                            <TableCell className="text-slate-500">{worker.email}</TableCell>
                            <TableCell>
                              <Badge
                                className={
                                  worker.status === "active"
                                    ? "bg-green-100 text-green-700"
                                    : worker.status === "suspended"
                                    ? "bg-red-100 text-red-700"
                                    : "bg-amber-100 text-amber-700"
                                }
                              >
                                {worker.status}
                              </Badge>
                            </TableCell>
                            <TableCell className="font-medium">{completedTasks}</TableCell>
                            <TableCell className="font-medium text-amber-600">
                              {(worker.points_balance || 0).toLocaleString()} pts
                            </TableCell>
                            <TableCell className="text-slate-500">
                              {worker.created_at ? new Date(worker.created_at).toLocaleDateString() : "-"}
                            </TableCell>
                            <TableCell>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleViewWorkerDetail(worker.user_id)}
                                data-testid={`view-worker-${worker.user_id}`}
                              >
                                <Eye className="w-4 h-4 mr-1" /> View
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Activity Tab */}
          <TabsContent value="activity">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Activity Feed */}
              <Card className="border-slate-200 lg:col-span-2">
                <CardHeader className="border-b border-slate-100">
                  <CardTitle className="font-heading text-lg flex items-center gap-2">
                    <Activity className="w-5 h-5 text-blue-500" />
                    Recent Activity
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0 max-h-[600px] overflow-y-auto">
                  {activityFeed.length === 0 ? (
                    <div className="p-8 text-center text-slate-500">
                      <Activity className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                      <p>No recent activity</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-slate-100">
                      {activityFeed.map((activity, index) => (
                        <div key={index} className="flex items-start gap-4 p-4 hover:bg-slate-50">
                          <div className="mt-1">{getActivityIcon(activity.type)}</div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-slate-700">{getActivityText(activity)}</p>
                            <p className="text-xs text-slate-400 mt-1">
                              {activity.timestamp ? new Date(activity.timestamp).toLocaleString() : "-"}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Quick Stats */}
              <Card className="border-slate-200">
                <CardHeader className="border-b border-slate-100">
                  <CardTitle className="font-heading text-lg flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-green-500" />
                    Today's Summary
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6 space-y-6">
                  {/* Today's stats from the latest daily data */}
                  {performanceTrends.daily.length > 0 && (() => {
                    const today = performanceTrends.daily[performanceTrends.daily.length - 1];
                    const yesterday = performanceTrends.daily[performanceTrends.daily.length - 2];
                    const subDiff = today && yesterday ? today.submissions - yesterday.submissions : 0;
                    const ptsDiff = today && yesterday ? today.points_earned - yesterday.points_earned : 0;
                    
                    return (
                      <>
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm text-slate-500">Submissions Today</p>
                            <p className="text-2xl font-bold text-slate-900">{today?.submissions || 0}</p>
                          </div>
                          <div className={`flex items-center ${subDiff >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                            {subDiff >= 0 ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
                            <span className="text-sm font-medium">{Math.abs(subDiff)}</span>
                          </div>
                        </div>
                        
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm text-slate-500">Approved Today</p>
                            <p className="text-2xl font-bold text-green-600">{today?.approved || 0}</p>
                          </div>
                        </div>
                        
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm text-slate-500">Points Earned Today</p>
                            <p className="text-2xl font-bold text-amber-600">{today?.points_earned || 0}</p>
                          </div>
                          <div className={`flex items-center ${ptsDiff >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                            {ptsDiff >= 0 ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
                            <span className="text-sm font-medium">{Math.abs(ptsDiff)}</span>
                          </div>
                        </div>
                      </>
                    );
                  })()}

                  <div className="pt-4 border-t border-slate-100">
                    <p className="text-sm text-slate-500 mb-2">This Week</p>
                    {performanceTrends.weekly.length > 0 && (() => {
                      const thisWeek = performanceTrends.weekly[performanceTrends.weekly.length - 1];
                      return (
                        <div className="grid grid-cols-2 gap-3">
                          <div className="text-center p-3 bg-blue-50 rounded-lg">
                            <p className="text-xl font-bold text-blue-600">{thisWeek?.submissions || 0}</p>
                            <p className="text-xs text-blue-700">Submissions</p>
                          </div>
                          <div className="text-center p-3 bg-green-50 rounded-lg">
                            <p className="text-xl font-bold text-green-600">{thisWeek?.approved || 0}</p>
                            <p className="text-xs text-green-700">Approved</p>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Performance Tab */}
          <TabsContent value="performance">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Performance Chart */}
              <Card className="border-slate-200 lg:col-span-2">
                <CardHeader className="border-b border-slate-100">
                  <CardTitle className="font-heading text-lg flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-blue-500" />
                    Performance Trends (Last 14 Days)
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  {chartData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <AreaChart data={chartData}>
                        <defs>
                          <linearGradient id="colorSubmissions" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.1}/>
                            <stop offset="95%" stopColor="#3B82F6" stopOpacity={0}/>
                          </linearGradient>
                          <linearGradient id="colorApproved" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#10B981" stopOpacity={0.1}/>
                            <stop offset="95%" stopColor="#10B981" stopOpacity={0}/>
                          </linearGradient>
                        </defs>
                        <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                        <YAxis tick={{ fontSize: 12 }} />
                        <Tooltip />
                        <Legend />
                        <Area type="monotone" dataKey="submissions" stroke="#3B82F6" fillOpacity={1} fill="url(#colorSubmissions)" name="Submissions" />
                        <Area type="monotone" dataKey="approved" stroke="#10B981" fillOpacity={1} fill="url(#colorApproved)" name="Approved" />
                      </AreaChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-[300px] flex items-center justify-center text-slate-500">
                      No performance data available
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Team Leaderboard */}
              <Card className="border-slate-200">
                <CardHeader className="border-b border-slate-100">
                  <CardTitle className="font-heading text-lg flex items-center gap-2">
                    <Award className="w-5 h-5 text-amber-500" />
                    Team Leaderboard
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  {workers.length === 0 ? (
                    <div className="p-8 text-center text-slate-500">
                      <p>No team members yet</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-slate-100">
                      {[...workers]
                        .sort((a, b) => (b.points_balance || 0) - (a.points_balance || 0))
                        .slice(0, 10)
                        .map((worker, index) => (
                          <div 
                            key={worker.user_id} 
                            className="flex items-center gap-4 p-4 hover:bg-slate-50 cursor-pointer"
                            onClick={() => handleViewWorkerDetail(worker.user_id)}
                          >
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                              index === 0 ? "bg-amber-100 text-amber-700" :
                              index === 1 ? "bg-slate-200 text-slate-700" :
                              index === 2 ? "bg-orange-100 text-orange-700" :
                              "bg-slate-100 text-slate-600"
                            }`}>
                              {index + 1}
                            </div>
                            <div className="flex-1">
                              <p className="font-medium">{worker.name || "Unknown"}</p>
                              <p className="text-sm text-slate-500">{worker.email}</p>
                            </div>
                            <p className="font-bold text-amber-600">{(worker.points_balance || 0).toLocaleString()} pts</p>
                            <ChevronRight className="w-4 h-4 text-slate-400" />
                          </div>
                        ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Team Stats Summary */}
              <Card className="border-slate-200">
                <CardHeader className="border-b border-slate-100">
                  <CardTitle className="font-heading text-lg flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-blue-500" />
                    Team Summary
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="space-y-6">
                    <div>
                      <div className="flex justify-between text-sm mb-2">
                        <span className="text-slate-600">Approval Rate</span>
                        <span className="font-medium">
                          {submissions.length > 0 
                            ? Math.round((approvedSubmissions.length / submissions.length) * 100)
                            : 0}%
                        </span>
                      </div>
                      <div className="w-full bg-slate-100 rounded-full h-2">
                        <div 
                          className="bg-green-500 h-2 rounded-full transition-all"
                          style={{ 
                            width: `${submissions.length > 0 ? (approvedSubmissions.length / submissions.length) * 100 : 0}%` 
                          }}
                        />
                      </div>
                    </div>

                    <div>
                      <div className="flex justify-between text-sm mb-2">
                        <span className="text-slate-600">Active Rate</span>
                        <span className="font-medium">
                          {workers.length > 0 
                            ? Math.round((activeWorkers / workers.length) * 100)
                            : 0}%
                        </span>
                      </div>
                      <div className="w-full bg-slate-100 rounded-full h-2">
                        <div 
                          className="bg-blue-500 h-2 rounded-full transition-all"
                          style={{ 
                            width: `${workers.length > 0 ? (activeWorkers / workers.length) * 100 : 0}%` 
                          }}
                        />
                      </div>
                    </div>

                    <div className="pt-4 border-t border-slate-100">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="text-center p-4 bg-green-50 rounded-lg">
                          <p className="text-2xl font-bold text-green-600">{approvedSubmissions.length}</p>
                          <p className="text-sm text-green-700">Approved</p>
                        </div>
                        <div className="text-center p-4 bg-red-50 rounded-lg">
                          <p className="text-2xl font-bold text-red-600">{rejectedSubmissions.length}</p>
                          <p className="text-sm text-red-700">Rejected</p>
                        </div>
                      </div>
                    </div>

                    <div className="pt-4 border-t border-slate-100">
                      <p className="text-sm text-slate-600 mb-2">Average Points per Gigster</p>
                      <p className="text-3xl font-bold text-slate-900">
                        {workers.length > 0 ? Math.round(totalPoints / workers.length).toLocaleString() : 0}
                        <span className="text-lg text-slate-500 ml-1">pts</span>
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </main>

      {/* Worker Detail Modal */}
      <Dialog open={showWorkerModal} onOpenChange={setShowWorkerModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                <User className="w-5 h-5 text-blue-600" />
              </div>
              {workerDetail?.worker?.name || "Worker Details"}
            </DialogTitle>
          </DialogHeader>
          
          {workerDetail && (
            <div className="space-y-6">
              {/* Worker Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-slate-500">Email</p>
                  <p className="font-medium">{workerDetail.worker?.email}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Status</p>
                  <Badge className={
                    workerDetail.worker?.status === "active" ? "bg-green-100 text-green-700" :
                    workerDetail.worker?.status === "suspended" ? "bg-red-100 text-red-700" :
                    "bg-amber-100 text-amber-700"
                  }>
                    {workerDetail.worker?.status}
                  </Badge>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Joined</p>
                  <p className="font-medium">
                    {workerDetail.worker?.created_at 
                      ? new Date(workerDetail.worker.created_at).toLocaleDateString() 
                      : "-"}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Country</p>
                  <p className="font-medium">{workerDetail.worker?.country || "-"}</p>
                </div>
              </div>

              {/* Stats Grid */}
              <div className="grid grid-cols-4 gap-3">
                <div className="text-center p-3 bg-blue-50 rounded-lg">
                  <p className="text-xl font-bold text-blue-600">{workerDetail.stats?.total_submissions || 0}</p>
                  <p className="text-xs text-blue-700">Total</p>
                </div>
                <div className="text-center p-3 bg-green-50 rounded-lg">
                  <p className="text-xl font-bold text-green-600">{workerDetail.stats?.approved || 0}</p>
                  <p className="text-xs text-green-700">Approved</p>
                </div>
                <div className="text-center p-3 bg-red-50 rounded-lg">
                  <p className="text-xl font-bold text-red-600">{workerDetail.stats?.rejected || 0}</p>
                  <p className="text-xs text-red-700">Rejected</p>
                </div>
                <div className="text-center p-3 bg-amber-50 rounded-lg">
                  <p className="text-xl font-bold text-amber-600">{workerDetail.stats?.points_balance || 0}</p>
                  <p className="text-xs text-amber-700">Points</p>
                </div>
              </div>

              {/* Activity by Day */}
              {workerDetail.activity_by_day && (
                <div>
                  <p className="text-sm font-medium text-slate-700 mb-3">Activity by Day of Week</p>
                  <ResponsiveContainer width="100%" height={150}>
                    <BarChart data={workerDetail.activity_by_day}>
                      <XAxis dataKey="day" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip />
                      <Bar dataKey="count" fill="#3B82F6" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* Recent Submissions */}
              <div>
                <p className="text-sm font-medium text-slate-700 mb-3">Recent Submissions</p>
                {workerDetail.submissions?.length > 0 ? (
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {workerDetail.submissions.slice(0, 10).map((sub, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                        <div>
                          <p className="text-sm font-medium">{sub.task?.title || "Unknown Task"}</p>
                          <p className="text-xs text-slate-500">
                            {sub.submitted_at ? new Date(sub.submitted_at).toLocaleDateString() : "-"}
                          </p>
                        </div>
                        <Badge className={
                          sub.status === "approved" ? "bg-green-100 text-green-700" :
                          sub.status === "rejected" ? "bg-red-100 text-red-700" :
                          "bg-amber-100 text-amber-700"
                        }>
                          {sub.status}
                        </Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-slate-500">No submissions yet</p>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Worker Comparison Modal */}
      <Dialog open={showCompareModal} onOpenChange={setShowCompareModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Scale className="w-5 h-5 text-blue-500" />
              Worker Comparison
            </DialogTitle>
          </DialogHeader>
          
          {comparisonData && comparisonData.length > 0 && (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Metric</TableHead>
                    {comparisonData.map(worker => (
                      <TableHead key={worker.worker_id} className="text-center">
                        {worker.name}
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <TableRow>
                    <TableCell className="font-medium">Status</TableCell>
                    {comparisonData.map(worker => (
                      <TableCell key={worker.worker_id} className="text-center">
                        <Badge className={
                          worker.status === "active" ? "bg-green-100 text-green-700" :
                          worker.status === "suspended" ? "bg-red-100 text-red-700" :
                          "bg-amber-100 text-amber-700"
                        }>
                          {worker.status}
                        </Badge>
                      </TableCell>
                    ))}
                  </TableRow>
                  <TableRow>
                    <TableCell className="font-medium">Points Balance</TableCell>
                    {comparisonData.map(worker => (
                      <TableCell key={worker.worker_id} className="text-center font-bold text-amber-600">
                        {worker.points_balance?.toLocaleString() || 0}
                      </TableCell>
                    ))}
                  </TableRow>
                  <TableRow>
                    <TableCell className="font-medium">Total Submissions</TableCell>
                    {comparisonData.map(worker => (
                      <TableCell key={worker.worker_id} className="text-center">
                        {worker.total_submissions}
                      </TableCell>
                    ))}
                  </TableRow>
                  <TableRow>
                    <TableCell className="font-medium">Approved</TableCell>
                    {comparisonData.map(worker => (
                      <TableCell key={worker.worker_id} className="text-center text-green-600">
                        {worker.approved}
                      </TableCell>
                    ))}
                  </TableRow>
                  <TableRow>
                    <TableCell className="font-medium">Rejected</TableCell>
                    {comparisonData.map(worker => (
                      <TableCell key={worker.worker_id} className="text-center text-red-600">
                        {worker.rejected}
                      </TableCell>
                    ))}
                  </TableRow>
                  <TableRow>
                    <TableCell className="font-medium">Approval Rate</TableCell>
                    {comparisonData.map(worker => (
                      <TableCell key={worker.worker_id} className="text-center">
                        <span className={worker.approval_rate >= 80 ? "text-green-600 font-bold" : "text-slate-700"}>
                          {worker.approval_rate}%
                        </span>
                      </TableCell>
                    ))}
                  </TableRow>
                  <TableRow>
                    <TableCell className="font-medium">Joined</TableCell>
                    {comparisonData.map(worker => (
                      <TableCell key={worker.worker_id} className="text-center text-slate-500">
                        {worker.joined || "-"}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableBody>
              </Table>

              {/* Visual Comparison Chart */}
              <div className="mt-6">
                <p className="text-sm font-medium text-slate-700 mb-3">Points Comparison</p>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={comparisonData}>
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Bar dataKey="points_balance" fill="#F59E0B" name="Points" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
